<template>
  <div class="referral-program-form">
    <HeadSection
      :breadcrumbs="breadcrumbs"
      :permissionOfSection="permissionOfSection"
    >
      <template #actions>
        <a-col v-if="!isOnlyView" :span="14" class="text-right">
          <a-button
            type="primary"
            size="large"
            :disabled="!createPermission || loading || initialLoading"
            @click="action"
          >
            <span class="px-2">{{ programId ? 'Save' : 'Create'}}</span>
          </a-button>
        </a-col>
      </template>
    </HeadSection>

    <div v-if="showInformation" class="d-flex mb-3" style="gap: 10px;">
      <div class="mt-1">
        <a-icon class="d-flex text-danger" type="info-circle" />
      </div>
      <span class="d-flex">
        To edit this program, you need to stop it first
      </span>
    </div>

    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection" :isBlockStyle="isEditMode">
      <div v-if="isEditMode" class="tabs-container">
        <a-tabs
          v-model="activeProgramTab"
          type="card"
          tab-position="top"
          @change="changeActiveProgramTab"
        >
          <a-tab-pane
            :key="generalInfoTab.key"
            force-render
          >
            <template #tab>
              <DefaultLabelTab
                :name="generalInfoTab.name"
                :error_validate="generalInfoTab.error_validate"
              />
            </template>
            <GeneralInfoForm
              ref="general-info"
              :defaultLanguage="defaultLanguage"
              :availableApplications="availableApplications"
              :availableLanguages="availableLanguages"
              :isOnlyView="isOnlyView"
              :errorFormValidate="generalInfoTab.error_validate"
              @changeLanguagesUsed="changeLanguagesUsed"
              @clearError="generalInfoTab.error_validate = false"
            />
          </a-tab-pane>
          <a-tab-pane
            :key="roleSettingsTab.key"
            force-render
          >
            <template #tab>
              <DefaultLabelTab
                :name="roleSettingsTab.name"
                :error_validate="roleSettingsTab.error_validate"
              />
            </template>
            <RoleSettingsForm
              ref="role-settings"
              :languagesUsed="languagesUsed"
              :isOnlyView="isOnlyView"
              :editPermission="editPermission"
              :errorFormValidate="roleSettingsTab.error_validate"
              @clearError="roleSettingsTab.error_validate = false"
            />
          </a-tab-pane>
        </a-tabs>
      </div>
      <GeneralInfoFormSkeleton v-else-if="initialLoading" />
      <GeneralInfoForm
        v-else
        ref="general-info"
        :defaultLanguage="defaultLanguage"
        :availableApplications="availableApplications"
        :availableLanguages="availableLanguages"
        :isOnlyView="isOnlyView"
      />
    </MainContainer>
    <div v-if="showRemove && isEditMode && deletePermission" class="mt-3">
      <div
        class="d-flex align-items-center text-danger cursor-pointer hover-underline"
        style="width: fit-content;"
        @click="toggleShowDeleteModal(true)"
      >
        <a-icon type="delete" class="mr-2" />
        <span>Delete referral program</span>
      </div>
    </div>
    <DefaultModal
      :show="showDeleteModal"
      title="Delete Referral Program"
      text="Are you sure you want to delete this referral program?"
      description="All associated data will be lost"
      :actionButton="{
        text: 'Delete',
        type: 'danger',
      }"
      :loading="loading"
      @close="toggleShowDeleteModal"
      @action="removeProgram"
    />
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import DefaultModal from '@/components/custom/modals/defaultModal'
import DefaultLabelTab from '@/views/apps/referral-program/components/DefaultLabelTab.vue'
import GeneralInfoForm from '@/views/apps/referral-program/referral-programs/form/GeneralInfoForm.vue'
import GeneralInfoFormSkeleton from '@/views/apps/referral-program/referral-programs/components/GeneralInfoFormSkeleton.vue'
import RoleSettingsForm from '@/views/apps/referral-program/referral-programs/form/RoleSettingsForm.vue'
import { getProgramParams } from '@/views/apps/referral-program/referral-programs/composables/useGetProgramParams.js'
import { getReferralApplications } from '@/views/apps/referral-program/composables/useGetReferralApplications.js'
import { getReferralLanguages } from '@/views/apps/referral-program/composables/useGetReferralLanguages.js'
import { defaultLanguageName } from '@/views/apps/referral-program/constants/defaultLanguageName.js'
import { waitForRef } from '@/composables/useWaitForRef.js'
import { programStatuses } from '@/views/apps/referral-program/referral-programs/composables/useProgramStatuses.js'
import { orderConditions } from '@/views/apps/referral-program/referral-programs/composables/useOrderConditions.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    DefaultLabelTab,
    GeneralInfoForm,
    GeneralInfoFormSkeleton,
    RoleSettingsForm,
    DefaultModal,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    activeProgramTab: 1,
    programId: null,
    currentProgram: null,
    generalInfoTab: { name: 'General info', key: 1, error_validate: false },
    roleSettingsTab: { name: 'Role settings', key: 2, error_validate: false },
    availableApplications: [],
    availableLanguages: [],
    languagesUsed: [],
    defaultLanguage: null,
    getApplicationsLoading: false,
    getAvailableLanguagesLoading: false,
    activeLanguage: 1,
    disableCopy: false,
    initialLoading: true,
    loading: false,
    showDeleteModal: false,
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    showRemove() {
      return this.currentProgram && this.currentProgram?.status !== programStatuses().active
    },
    isOnlyView() {
      if (!this.currentProgram) {
        return false
      }
      return this.currentProgram?.status !== programStatuses().inactive
    },
    isEditMode() {
      return !!this.programId
    },
    breadcrumbs() {
      if (this.currentProgram) {
        return [
          { name: 'Referral programs', link: '/referral/programs' },
          { name: this.currentProgram?.name, link: `/referral/programs/${this.currentProgram.id}` },
          { name: this.isOnlyView ? 'View referral program' : 'Edit referral program', link: undefined },
        ]
      }
      return [
        { name: 'Referral programs', link: '/referral/programs' },
        { name: 'Create referral program', link: undefined },
      ]
    },
    programStatusName() {
      return this.currentProgram?.status || ''
    },
    showInformation() {
      if (!this.currentProgram) {
        return false
      }

      const now = this.$moment()
      const start = this.$moment(this.currentProgram?.started_at)

      if (this.programStatusName === programStatuses().active && now.isSameOrBefore(start)) {
        return true
      }
      return false
    },
  },
  methods: {
    toggleShowDeleteModal(status = false) {
      this.showDeleteModal = status
    },
    changeLanguagesUsed(langs) {
      this.languagesUsed = langs.sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
    },
    changeActiveProgramTab(e) {
      if (Object.keys(this.$route.query).length > 0) {
        this.$router.push({ path: this.$route.path, query: {} })
      }
    },
    async checkingForTabFilling() {
      const isValidGeneralInfo = await this.$refs['general-info'].validateFields()
      if (!this.isEditMode) {
        return isValidGeneralInfo
      }

      const isValidRoleSettings = await this.$refs['role-settings'].validateFields()

      if (!isValidGeneralInfo) {
        this.generalInfoTab.error_validate = true
      }

      if (!isValidRoleSettings) {
        this.roleSettingsTab.error_validate = true
      }

      return isValidGeneralInfo && isValidRoleSettings
    },
    getPayloadData() {
      const generalInfoPayload = this.$refs['general-info'].getPayload()
      if (!this.isEditMode) {
        return generalInfoPayload
      }
      const roleSettingsPayload = this.$refs['role-settings'].getPayload()
      return { ...generalInfoPayload, ...roleSettingsPayload }
    },
    async action() {
      // Нужно чтобы отобразить в каких табах есть ошибки валидации
      const isValidTabs = await this.checkingForTabFilling()

      if (!isValidTabs) {
        this.$notification.error({
          message: 'Please fill in all required fields',
        })
        return false
      }

      const data = this.getPayloadData()
      if (this.isEditMode) {
        this.updateProgram(data)
        return
      }
      this.createProgram(data)
    },
    async createProgram(payload) {
      try {
        this.loading = true
        const url = `${referralApiUrl}/referral-programs`
        const response = await apiClient.post(url, payload)
        const { data } = response?.data
        if (response?.status >= 200 && response?.status < 300) {
          this.$notification.success({
            message: 'Referral program successfully created',
            description: '',
          })
          this.$router.push(`/referral/programs/${data.id}/edit/?tab=2`)
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        getErrorMessage(e)
      } finally {
        this.loading = false
      }
    },
    async updateProgram(data) {
      try {
        this.loading = true
        const url = `${referralApiUrl}/referral-programs/${this.programId}`
        const response = await apiClient.put(url, data)
        if (response?.data?.data) {
          this.$notification.success({
            message: 'Referral program successfully updated',
            description: '',
          })
          this.$router.push(`/referral/programs/${this.programId}`)
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async removeProgram() {
      try {
        this.loading = true
        const url = `${referralApiUrl}/referral-programs/${this.programId}`
        const response = await apiClient.delete(url)
        if (response?.status >= 200 && response?.status < 300) {
          this.$notification.success({
            message: 'Referral program successfully removed',
            description: '',
          })
          this.$router.push('/referral/programs')
          return
        }
        getErrorMessage(response?.data)
      } finally {
        this.loading = false
      }
    },
    async getProgramParams() {
      const data = await getProgramParams(this.programId)

      if (data) {
        const referralOrderedConditions = data.referral_settings ? data.referral_settings.conditions?.sort((a, b) => orderConditions().indexOf(a.type) - orderConditions().indexOf(b.type)) : []
        const affiliateOrderedConditions = data.affiliate_settings ? data.affiliate_settings.conditions?.sort((a, b) => orderConditions().indexOf(a.type) - orderConditions().indexOf(b.type)) : []
        const finalData = {
          ...data,
          referral_settings: data.referral_settings ? { ...data.referral_settings, conditions: referralOrderedConditions } : null,
          affiliate_settings: data.affiliate_settings ? { ...data.affiliate_settings, conditions: affiliateOrderedConditions } : null,
        }

        this.currentProgram = finalData
        this.setFormValues(finalData)
        return
      }
      this.$router.push('/referral/programs')
    },
    async getApplications() {
      try {
        this.getApplicationsLoading = true
        const applications = await getReferralApplications() || []
        if (applications?.length) {
          this.availableApplications = applications.map(app => {
            return {
              ...app,
              min_app_version: '',
              max_app_version: '',
              application_id: app.id,
            }
          })
        }
      } finally {
        this.getApplicationsLoading = false
      }
    },
    async getAvailableLanguages() {
      try {
        this.getAvailableLanguagesLoading = true
        const allLanguages = await getReferralLanguages() || []
        if (allLanguages) {
          this.availableLanguages = allLanguages.map(lang => {
            const langObject = {
              id: lang.id,
              name: lang.name,
              is_default: false,
            }
            if (lang.name === defaultLanguageName) {
              langObject.is_default = true
            }
            return langObject
          })
          this.setDefaultLanguage()
        }
      } finally {
        this.getAvailableLanguagesLoading = false
      }
    },
    setDefaultLanguage() {
      this.defaultLanguage = this.availableLanguages.find(lang => lang.name === defaultLanguageName)
    },
    async setFormValues(data) {
      await waitForRef('general-info', this)
        .then((generalInfoRef) => {
          generalInfoRef.setFormValues(data)
        })
        .catch((err) => {
          console.warn('Не удалось получить $refs.filters:', err)
        })

      if (!this.isEditMode) {
        return
      }
      await waitForRef('role-settings', this)
        .then((roleSettingsRef) => {
          roleSettingsRef.setFormValues(data)
        })
        .catch((err) => {
          console.warn('Не удалось получить $refs.filters:', err)
        })
    },
  },
  async mounted() {
    this.programId = this.$route.params.id

    try {
      this.initialLoading = true
      const requests = [this.getAvailableLanguages(), this.getApplications()]

      if (this.programId) {
        requests.push(this.getProgramParams())
      }

      await Promise.all(requests)
    } finally {
      this.$nextTick(() => {
        this.initialLoading = false
        if (this.$route.query?.tab) {
          this.activeProgramTab = Number(this.$route.query?.tab)
        }
      })
    }
  },
}
</script>

<style lang="scss">
.referral-program-form {
  .tabs-container {
    overflow: hidden;
    height: 100%;
  }
  .tabs-container > .ant-tabs {
    height: 100%;
  }
  .tabs-container > .ant-tabs-card > .ant-tabs-content {
    height: 100%;
    margin-top: -16px;
  }

  .tabs-container > .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane {
    background: #fff !important;
    padding: 16px;
  }
  .tabs-container > .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane-active {
    height: 100%;
  }
  .tabs-container > .ant-tabs-card > .ant-tabs-bar {
    border-color: #fff !important;
  }

  .tabs-container > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab {
    border-color: transparent;
    background: transparent;
    margin-right: 0 !important;
  }

  .tabs-container > .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab-active {
    border-color: #fff !important;
    background: #fff !important;
  }
}
</style>
